export function makeCompareStrings(field) {
  return (a, b) => {
    const aLower = a[field].toLowerCase();
    const bLower = b[field].toLowerCase();
    if (aLower < bLower) {
      return -1;
    }
    if (aLower > bLower) {
      return 1;
    }
    return 0;
  };
}

export const getCookie = name => {
  const cookies = document.cookie.split(' ').map(cookie => {
    const index = cookie.indexOf('=');
    if (index < 0) return { name: '', value: null };

    const name = cookie.slice(0, index);
    let value = cookie.slice(index + 1);
    value = value.endsWith(';') ? value.slice(0, -1) : value;
    return { name, value };
  });
  const cookie = cookies.find(cookie => cookie.name === name);

  return cookie ? cookie.value : null;
};

export const makeId = length => {
  let result = '';
  let characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

/**
 * Allows to get rid of imprecise decimal part.
 * For e.g. 2.2800000000000002 becomes 2.28.
 * @param value
 * @return {number}
 */
export const fixFloat = value => Math.round(value * 10e9) / 10e9;

function isObject(val) {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

/**
 * Applies changed values iteratively
 * @param newValues
 * @param currentChanges
 * @returns {*}
 */
function applyFormikChanges(newValues, currentChanges) {
  let result;
  if (isObject(newValues)) {
    result = { ...newValues };
    Object.keys(result).forEach(key => {
      if (currentChanges[key]) {
        result[key] = applyFormikChanges(result[key], currentChanges[key]);
      }
    });
  } else if (Array.isArray(newValues)) {
    result = [...newValues];
    const newItemsMap = new Map(result.map((item, index) => [item._id, index]));
    currentChanges.forEach(item => {
      if (!item._id) {
        // Added
        result.push(item);
      } else {
        const index = newItemsMap.get(item._id);
        if (index != null)
          if (item._isDeleted) {
            // Delete new item if it was deleted by user. Marking it as deleted
            // instead of actual delete to preserve array indexes
            result[index]._isDeleted = true;
          } else {
            // Updated
            result[index] = applyFormikChanges(result[index], item);
          }
      }
    });
    // Remove marked deleted items
    result = result.filter(item => !item._isDeleted);
  } else {
    result = currentChanges;
  }
  return result;
}

/**
 * Compares initial values with (form) values and returns only the changes.
 * Useful with Formik, since it returns all values even the ones which wasn't changed.
 * Constrains:
 * - `initialValues` does not contain fields which does not exist on the `values`.
 * - args does not have nested arrays
 * - objects in arrays contain _id field
 * @param initialValues  Initial values
 * @param values New values
 * @returns
 */
export const getFormikChangedValues = (initialValues, values) => {
  const isArray = Array.isArray(initialValues);
  if (isArray || isObject(initialValues)) {
    if (isArray) {
      const isPrimitiveArray =
        !isObject(initialValues[0]) && !isObject(values[0]);
      if (isPrimitiveArray) {
        if (
          initialValues.length !== values.length ||
          initialValues.some((item, index) => item !== values[index])
        ) {
          return values;
        }
      } else {
        const valuesIds = new Set();
        const initialValuesMap = new Map(
          initialValues.map((item, index) => [item._id, index])
        );
        const result = values.reduce((updatedItems, item) => {
          valuesIds.add(item._id);
          const initialItem = initialValues[initialValuesMap.get(item._id)];
          const changes = getFormikChangedValues(initialItem, item);
          if (changes !== undefined) {
            if (!updatedItems) {
              updatedItems = [];
            }
            updatedItems.push({ _id: item._id, ...changes });
          }
          return updatedItems;
        }, undefined);

        // Deleted values
        return initialValues.reduce((deletedItems, item) => {
          if (!valuesIds.has(item._id)) {
            if (!deletedItems) {
              deletedItems = [];
            }
            deletedItems.push({ _id: item._id, _isDeleted: true });
          }
          return deletedItems;
        }, result);
      }
    } else {
      return Object.keys(values).reduce((result, key) => {
        const changes = getFormikChangedValues(initialValues[key], values[key]);
        if (changes !== undefined) {
          if (!result) {
            result = {};
          }
          result[key] = changes;
        }
        return result;
      }, undefined);
    }
  } else if (initialValues !== values) {
    return values;
  }
  return undefined;
};

/**
 * Merges user changes into new values
 * @param newValues
 * @param initialValues
 * @param userValues
 * @returns Object | null
 */
export const mergeFormikValues = (initialValues, userValues, newValues) => {
  // Exit early if there is no incoming changes or form hasn't been touched
  if (!userValues) {
    return newValues;
  }

  // Compare the current formik values and with old event
  const currentChanges = getFormikChangedValues(initialValues, userValues);
  if (currentChanges === undefined) {
    return newValues;
  }

  // Combine
  return applyFormikChanges(newValues, currentChanges);
};
